import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import { useUser } from '../../utils/userContext';
import moment from 'moment';
import { Button, Modal } from 'react-bootstrap';
import useConfiguration from '../../utils/ConfigurationContext';
import Select from 'react-select';
import DateRangePicker from "react-bootstrap-daterangepicker";
import useHandleErrors from '../../utils/user.utils';
import wwImg from '../../assets/globalImgs/WW-360-Logo.png'



const LeaveStatus = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const { user } = useUser();
    const config = useConfiguration();
    const [showModal, setShowModal] = useState(false);
    const [record, setRecord] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem("token");
    const handleErrors = useHandleErrors();
    const [formData, setFormData] = useState({
        leaveType: '',
        startDate: moment().format("DD-MM-YYYY"),
        endDate: moment().format("DD-MM-YYYY"),
        daysOfWeek: [],
        description: '',
        document: null,
        alternateContactPerson: '',
        teamLeadEmail: '',
    });
    const hasFetchedRef = useRef(false);

    const getLeaveStatus = async () => {
        setLoading(true)
        if (user && user.OfficialEmail) {
            try {
                const path = `/LeaveTracker/list?emailId=${user.OfficialEmail}`;
                const URL = process.env.REACT_APP_BE_URL + path;
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                });
                if (response.status === 200) {
                    const responseData = response.data.myLeaveRequests || [];
                    setData(responseData);
                    setFilteredData(responseData);
                }
                else {
                    handleErrors(response);
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 404) {
                        setData([]);
                        setFilteredData([]);
                    } else {
                        handleErrors(error.response);
                    }
                } else {
                    toast.error("Something went wrong. Please try again later.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!hasFetchedRef.current) {
            getLeaveStatus();
            
            hasFetchedRef.current = true;
        }
    }, [user]);

    useEffect(() => {
        if (!hasFetchedRef.current) {
            handleCancelLeave();            
            hasFetchedRef.current = true;
        }
    }, [user]);

    useEffect(() => {
        const preloading = document.getElementById("preloading");
        setTimeout(() => {
            if (preloading) {
                preloading.style.display = "none";
            }
        }, 400);
    }, []);


    const fetchRecordDetails = async (id) => {
        try {
            const path = `/LeaveTracker/details?emailId=${user?.OfficialEmail}&leaveId=${id}`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`
                },
            });
            if (response.status === 200) {
                const data = response.data;
                setFormData({
                    userEmailId: data.userEmailId,
                    leaveId: data.leaveId,
                    leaveType: data.leaveType || '',
                    startDate: data.startDate || moment().format("DD-MM-YYYY"),
                    endDate: data.endDate || moment().format("DD-MM-YYYY"),
                    daysOfWeek: data.recurrence || [],
                    description: data.description || '',
                    noOfhours: 8,
                    alternateContactPerson: data.alternateContactPerson || '',
                    teamLeadEmail: data.managerApprovalStatus[0].emailId || '',
                    medicalDocuments: data.medicalDocuemnts || []
                });
                setShowModal(true);
            } else {
                handleErrors(response)
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    };

    const handleCancelLeave = async (record) => {
        try {
            const formDataToSubmit = new FormData();
            const jsonPayload = {
                leaveId: record.leaveId,
                userEmail: user?.OfficialEmail,
                leaveType: record.leaveType,
                description: record.description,
                startDate: moment(record.startDate).format("YYYY-MM-DD"),
                endDate: moment(record.endDate).format("YYYY-MM-DD"),
                noOfhours: 8,
                teamLeadEmail: record.teamLeadEmail || user?.ManagerEmail,
                alternateContactPerson: record.alternateContactPerson,
                Recurrence: record.daysOfWeek || [],
                Status: "Cancelled" // <-- Set status to cancelled
            };
            formDataToSubmit.append("jsonPayload", JSON.stringify(jsonPayload));
            formDataToSubmit.append("MedicalDocuments", uploadedFiles);
            const path = "/LeaveTracker/update";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.put(URL, formDataToSubmit, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response.status === 200) {
                getLeaveStatus();
                toast.success("Leave Cancelled Successfully", {
                    position: toast.POSITION.TOP_CENTER,
                });
                setShowModal(false);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    };

    const sendEmail = () => {
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Leave_Status');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year Ascend
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        
        // Download the Excel file
        saveAs(data, `WW360-Leave_Status_Report_${formattedDate}.xlsx`);

        // Open the email client
        const mailtoLink = `mailto:?subject=Leave Report List&body=Please find the attached file.`;
        toast.info('The Excel file has been downloaded. Please attach it to your email.', {
            position: toast.POSITION.TOP_CENTER,
        });

        setTimeout(() => {
            window.location.href = mailtoLink;
        }, 3000);
    };

    const handleExport = () => {
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Leave_Status');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        saveAs(data, `WW360-Leave_Status_Report_${formattedDate}.xlsx`);
    };

    const handleShare = async () => {
        try {
            await navigator.share({
                title: 'Claim List',
                text: 'Check out the Claim list',
                url: window.location.href
            });
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    const handleDownloadAll = (documents) => {
        documents.forEach(doc => {
            const link = document.createElement('a');
            link.href = doc;
            link.download = decodeURIComponent(doc.substring(doc.lastIndexOf('/') + 1));
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        });
    };

    const handleDownloadPDF = (record) => {
        const doc = new jsPDF();

        // Define page margins and dimensions
        const marginLeft = 10;
        const marginRight = 10;
        const pageWidth = doc.internal.pageSize.width;

        // Header
        // Add logo on the left side
        const imgData = wwImg; // Replace with your actual logo path or base64 string
        doc.addImage(imgData, 'PNG', marginLeft, 10, 40, 15); // Logo positioned on the left

        // Add timestamp on the right side in small size
        const timestamp = new Date().toLocaleString();
        doc.setFontSize(8);
        doc.text(timestamp, pageWidth - marginRight, 15, { align: 'right' });

        // Add heading
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(37, 183, 64); // Set text color to #25b740 (RGB: 37, 183, 64)
        doc.text("Leave Status Form", pageWidth / 2, 30, { align: 'center' });

        // Reset text color to black for other text
        doc.setTextColor(0, 0, 0);

        // Add employee details
        doc.setFontSize(10);
        doc.setFont("helvetica", "Bold");
        doc.setTextColor(0, 79, 124); // Set text color to #004f7c (RGB: 0, 79, 124)
        const employeeDetailsStartY = 45; // Start Y position for employee details
        doc.text("Employee Details", marginLeft, employeeDetailsStartY);
        doc.setTextColor(0, 0, 0);
        // Employee details
        const employeeDetails = [
            { label: 'Employee ID', value: user.EmployeeId },
            { label: 'Employee Email', value: user.OfficialEmail }
        ];

        // Draw employee details
        employeeDetails.forEach((detail, index) => {
            const y = employeeDetailsStartY + 10 + (index * 10);
            doc.text(`${detail.label}: ${detail.value}`, marginLeft, y);
        });

        // Draw a thin line above the table
        const lineY = employeeDetailsStartY + 10 + (employeeDetails.length * 10) + 5;
        doc.setDrawColor(0, 0, 0);
        // doc.setLineWidth(0.5); // Standard line width
        doc.line(marginLeft, lineY, pageWidth - marginRight, lineY);

        // Add "Details" heading
        const detailsHeadingY = lineY + 10; // Y position for "Details" heading
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 79, 124); // Set text color to #004f7c (RGB: 0, 79, 124)
        doc.text("Leave Status Details", marginLeft, detailsHeadingY);

        // Define table headers
        const headers = ['Field', 'Value'];
        doc.setTextColor(0, 0, 0);
        const rows = [
            { key: 'Leave ID', value: `${record.leaveId}` },
            { key: 'Leave Type', value: `${record.leaveType}` },
            { key: 'Start Date', value: moment(`${record.startDate}`).format("DD-MM-YYYY") },
            { key: 'End Date', value: moment(`${record.endDate}`).format("DD-MM-YYYY") },
            { key: 'Status', value: `${record.overallStatus}` },
        ];
        console.log("record", record)

        // Define table positions and dimensions
        const tableStartY = detailsHeadingY + 20; // Add space below "Details" heading
        const rowHeight = 10;
        const columnWidth = {
            field: 80,
            value: pageWidth - 100 // Adjust as needed to fit the page width
        };

        // Draw table headers (Field and Value)
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setDrawColor(0, 0, 0);
        // doc.setLineWidth(0.5); // Normal line width

        // Draw header cell borders
        doc.rect(marginLeft, tableStartY - rowHeight, columnWidth.field, rowHeight); // Field header cell border
        doc.rect(marginLeft + columnWidth.field, tableStartY - rowHeight, columnWidth.value, rowHeight); // Value header cell border

        // Add header text
        doc.text(headers[0], marginLeft + 5, tableStartY - rowHeight / 2 + 3); // "Field" header text
        doc.text(headers[1], marginLeft + columnWidth.field + 5, tableStartY - rowHeight / 2 + 3); // "Value" header text

        // Reset font to normal for table data rows
        doc.setFont("helvetica", "normal");

        // Draw table rows
        rows.forEach((row, index) => {
            const y = tableStartY + (index * rowHeight) + rowHeight / 2; // Adjusted to center the text vertically
            doc.text(row.key, marginLeft + 5, y); // Field value
            doc.text(row.value, marginLeft + columnWidth.field + 25, y, { align: 'right' }); // Value
            // Draw row borders
            doc.setDrawColor(0, 0, 0);
            doc.rect(marginLeft, y - rowHeight / 2, columnWidth.field, rowHeight); // Row border
            doc.rect(marginLeft + columnWidth.field, y - rowHeight / 2, columnWidth.value, rowHeight); // Row border
        });

        // Add "Approved By" and "Name" text
        const approvedByY = tableStartY + (rows.length * rowHeight) + 20; // Position below the table
        const nameText = `${record.approvedBy}` || 'Admin';
        const approvedByText = "Approved By";

        // Add the text aligned to the right side
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");

        // // "Name" text
        doc.text(nameText, pageWidth - marginRight - 50, approvedByY); // Adjust -50 to move it closer or further from the right

        // "Approved By" text below "Name"
        doc.text(approvedByText, pageWidth - marginRight - 50, approvedByY + 10); // 10 units below "Name"

        // Add Digital Signature (Left Side)
        const SigimgData = record?.digitalSignature; // Get the digital signature
        const employeeName = user?.FirstName || "Employee Name"; // Fallback employee name

        if (SigimgData) {
            // Add the digital signature as an image if it exists
            doc.addImage(SigimgData, 'PNG', marginLeft, approvedByY, 50, 50); // Adjust width and height as needed
        } else {
            // Add the employee's name as a fallback
            doc.setFontSize(12); // Set font size for the name
            doc.text(employeeName, marginLeft, approvedByY + 10); // Adjust Y position for better alignment
        }

        // Add the signatory name below the image
        const signatureName = "Authorized Signatory";
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(signatureName, marginLeft, approvedByY + 60); // Adjust 60 to position the text below the image



        // Add footer with timestamp and additional data, aligned left and right
        doc.setFontSize(10);
        const footerTimestamp = `Timestamp: ${timestamp}`;
        const footerText = "Generated By WorkWise360";
        doc.text(footerTimestamp, marginLeft, 280); // Left-aligned footer
        doc.text(footerText, pageWidth - marginRight, 280, { align: 'right' }); // Right-aligned footer


        // Save the PDF
        doc.save(`Leave_${record.leaveId}.pdf`);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (type === 'checkbox') {
            setFormData((prevData) => ({
                ...prevData,
                daysOfWeek: checked
                    ? [...prevData.daysOfWeek, name]
                    : prevData.daysOfWeek.filter((day) => day !== name),
            }));
        } else if (type === 'file') {
            setFormData((prevData) => ({
                ...prevData,
                document: files[0],
            }));
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: value,
            }));
        }
    };

    const handleSubmit = async () => {
        const formDataToSubmit = new FormData();
        const jsonPayload = {
            leaveId: formData.leaveId,
            userEmail: user?.OfficialEmail,
            leaveType: formData.leaveType,
            description: formData.description,
            startDate: moment(formData.startDate).format("YYYY-MM-DD"),
            endDate: moment(formData.endDate).format("YYYY-MM-DD"),
            noOfhours: 8,
            teamLeadEmail: formData.teamLeadEmail || user?.ManagerEmail,
            alternateContactPerson: formData.alternateContactPerson,
            Recurrence: formData.daysOfWeek // Make sure daysOfWeek is included in the payload
        };

        // Append the JSON payload
        formDataToSubmit.append("jsonPayload", JSON.stringify(jsonPayload));

        // Append the medical documents
        formDataToSubmit.append("MedicalDocuments", uploadedFiles);


        try {
            const path = "/LeaveTracker/update";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.put(URL, formDataToSubmit, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response.status === 200) {
                getLeaveStatus()
                toast.success("Leave Updated Success", {
                    position: toast.POSITION.TOP_CENTER,
                });
                setShowModal(false);
            }
            else {
                handleErrors(response)
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    };

    const handleUpdateShow = (record) => {
        setRecord(record);
        fetchRecordDetails(record.id);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setRecord(null);
    };

    const handleClick = () => {
        document.querySelector('input[name="dateRange"]').focus();
    };

    const leaveTypeOptions = config?.leaveManagement?.holidayTypes?.map(department => ({
        value: department,
        label: department,
    }));

    const ClearIndicator = () => null;

    // const handleEvent = ({ startDate, endDate }) => {
    //     // Update formData based on DateRangePicker changes
    //     setFormData({
    //         startDate: moment(startDate).format('DD-MM-YYYY'),
    //         endDate: moment(endDate).format('DD-MM-YYYY'),
    //     });
    // };

    const handleEvent = (event, picker) => {
        const startDate = moment(picker.startDate).format('YYYY-MM-DD');
        const endDate = moment(picker.endDate).format('YYYY-MM-DD');
        setFormData((prevData) => ({
            ...prevData,
            startDate,
            endDate,
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    useEffect(() => {
        // Filter employees based on the search query
        setFilteredData(
            data?.filter(data =>
                data.leaveType.toLowerCase().includes(searchQuery.toLowerCase()) ||
                data.overallStatus.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [searchQuery, data]);

    const handleRecordsPerPageChange = (event) => {
        setRecordsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredData.length / recordsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);


    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col">
                            <h3 className="title mb-2">Leave Status</h3>
                        </div>
                        <div className="col-auto">
                            <Link to={'/SubmitTimesheet'} className="btn btn-primary mt-lg-0 mt-4">
                                <i className="ri-add-circle-line"></i>Timesheet
                            </Link>
                        </div>
                    </div>

                    {data?.length === 0 ? (
                        <div class="container">
                            <div class="row vh-50 justify-content-center align-items-center">
                                <div class="col-auto">
                                    <div class="no-data-wrapper">
                                        <div class="no-data-found">
                                            <i class="ri-file-damage-fill"></i>
                                            <div class="bounce"><i class="ri-search-eye-line"></i></div>
                                            <p>No Data Found</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="row align-items-center justify-content-center">
                                <div className="col-lg-5 col-md-8 col-sm-10">
                                    <div className="container-fluid row-rounded mb-0">
                                        <div className="row px-2 py-0">
                                            <div className="input-group mb-0">
                                                <input
                                                    type="search"
                                                    className="form-control search-input"
                                                    placeholder="Keyword search..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                                <div className="input-group-append">
                                                    <button className="btn btn-primary" type="button" title="Search" rel="tooltip">
                                                        <i className="ri-menu-search-line"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <small className="form-text text-muted text-center mt-0">
                                        <i className="ri-information-2-fill text-info"></i> Allows: Leave Type  - Status
                                    </small>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card mb-4">
                                        <div className="card-body">

                                            {/* user view */}
                                            <div className="row">
                                                <div className="col">
                                                    <h5 className="title mt-1 mb-0">Leave Report</h5>
                                                </div>
                                                <div className="col-auto">
                                                    <button type="button" title="Email" className="btn btn-light btn-circle ml-2" onClick={() => sendEmail('')}>
                                                        <i className="ri-mail-send-fill"></i>
                                                    </button>
                                                    <button type="button" title="Share" className="btn btn-light btn-circle ml-2" onClick={handleShare}>
                                                        <i className="ri-share-fill"></i>
                                                    </button>
                                                    <button type="button" title="Export" className="btn btn-light btn-circle ml-2" onClick={handleExport}>
                                                        <i className="ri-export-fill"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="row mt-3">
                                                <div className="col-md-12">
                                                    <div className="table-responsive leave-information">
                                                        <table className="table grid">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>S.No.</th>
                                                                    <th>Leave Type  </th>
                                                                    <th>Start Date  </th>
                                                                    <th>End Date  </th>
                                                                    <th>Status  </th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {currentRecords?.map((record, index) => (
                                                                    <tr key={record.leaveId}>
                                                                        <td>{index + 1 + indexOfFirstRecord}</td>
                                                                        <td>{record.leaveType}</td>
                                                                        <td>{moment(record.startDate).format("DD-MM-YYYY")}</td>
                                                                        <td>{moment(record.endDate).format("DD-MM-YYYY")}</td>
                                                                        <td>
                                                                            {record.overallStatus.trim().toLowerCase() === 'approved' ? (
                                                                                <span className="badge border border-success text-success status">
                                                                                    <i className="ri-thumb-up-fill"></i> {record.overallStatus.trim()}
                                                                                </span>
                                                                            ) : record.overallStatus.trim().toLowerCase() === 'submitted' ? (
                                                                                <span className="badge border border-secondary text-secondary status">
                                                                                    <i className="ri-thumb-up-fill"></i> {record.overallStatus.trim()}
                                                                                </span>
                                                                            ) : record.overallStatus.trim().toLowerCase() === 'rejected' ? (
                                                                                <span className="badge border border-danger text-danger status">
                                                                                    <i className="ri-thumb-down-fill"></i> {record.overallStatus.trim()}
                                                                                </span>
                                                                            ): record.overallStatus.trim().toLowerCase() === 'cancelled' ? (
                                                                                <span className="badge border border-danger text-danger status">
                                                                                    <i className="ri-thumb-down-fill"></i> {record.overallStatus.trim()}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="badge border border-warning text-warning status">
                                                                                    <i className="ri-hourglass-fill"></i> Pending
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        {record?.overallStatus.trim().toLowerCase() === 'submitted' ? (
                                                                            // <td className="text-center">
                                                                            //     <Button variant="link" className="text-info" onClick={() => handleUpdateShow(record)}>
                                                                            //         <i className="ri-list-settings-line"></i>
                                                                            //     </Button>
                                                                            // </td>
                                                                            <td className="text-center">
                                                                                <Button
                                                                                    variant="link"
                                                                                    className="text-info"
                                                                                    onClick={async () => {
                                                                                        const data = await fetchRecordDetails(record.leaveId);
                                                                                        if (data) {
                                                                                            setFormData(data);
                                                                                            handleUpdateShow(record);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <i className="ri-list-settings-line"></i>
                                                                                </Button>
                                                                            </td>
                                                                        ) : record?.overallStatus.trim().toLowerCase() === 'cancelled' ? (
                                                                            <td className="text-center">
                                                                                <button
                                                                                    className="btn btn-link text-info btn-action custom-dropdown-btn"
                                                                                    type="button"
                                                                                    title="Download"
                                                                                    rel="tooltip"
                                                                                    onClick={() => handleDownloadPDF(record)}
                                                                                >
                                                                                    <i className="ri-download-2-line"></i>
                                                                                </button>
                                                                            </td>
                                                                        ) : (
                                                                            <>
                                                                                <td className="text-center">
                                                                                    <button
                                                                                        className="btn btn-link text-info btn-action custom-dropdown-btn"
                                                                                        type="button"
                                                                                        title="Download"
                                                                                        rel="tooltip"
                                                                                        onClick={() => handleDownloadPDF(record)}
                                                                                    >
                                                                                        <i className="ri-download-2-line"></i>
                                                                                    </button>
                                                                                    <button
                                                                                        variant="link"
                                                                                        className="btn btn-link text-danger btn-action custom-dropdown-btn"
                                                                                        type="button"
                                                                                        title="Cancel"
                                                                                        rel="tooltip"
                                                                                        onClick={() => handleCancelLeave(record)}
                                                                                    >
                                                                                        <i className="ri-close-circle-line text-danger"></i>
                                                                                    </button>
                                                                                </td>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                <div className="col-md-12">
                                                    <div className="container-fluid">
                                                        <div className="row border-top pt-3">
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Records</p>
                                                            </div>
                                                            <div className="col-1 record-drop">
                                                                <select className="form-control" value={recordsPerPage} onChange={handleRecordsPerPageChange}>
                                                                    <option value="5">5</option>
                                                                    <option value="10">10</option>
                                                                    <option value="15">15</option>
                                                                    <option value="20">20</option>
                                                                    <option value="25">25</option>
                                                                </select>
                                                            </div>
                                                            <div className="col">
                                                                <nav>
                                                                    <ul className="pagination justify-content-center">
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-double-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        {Array.from({ length: totalPages }, (_, index) => (
                                                                            <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(index + 1)}>
                                                                                    {index + 1}
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                                                <i className="ri-arrow-right-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(totalPages)}>
                                                                                <i className="ri-arrow-right-double-fill"></i>
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </nav>
                                                            </div>
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Modal show={showModal} onHide={handleModalClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Update Leave</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h5 className="title">
                        <small>Employee Email - <span>{formData.userEmailId}</span></small>
                    </h5>
                    <hr />
                    <div className="row">
                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="leaveType" className="required">Leave Type</label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    name="leaveType"
                                    value={leaveTypeOptions?.find(
                                        (option) => option.value === formData.leaveType
                                    )}
                                    onChange={(selectedOption) =>
                                        setFormData({
                                            ...formData,
                                            leaveType: selectedOption ? selectedOption.value : '',
                                        })
                                    }
                                    options={leaveTypeOptions}
                                    components={{ ClearIndicator }} // Hides the clear indicator
                                    isClearable
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base, state) => ({
                                            ...base,
                                            zIndex: 1,
                                            '&:hover': {
                                                borderColor: state.isFocused ? '#2684FF' : '#aaa',
                                            },
                                        }),
                                        menu: base => ({ ...base, zIndex: 9999 })
                                    }}
                                // menuPosition="relative" 
                                />
                            </div>

                        </div>
                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="dateRange" className="required">Date Range</label>
                                <div className="input-group dateRange">
                                    <DateRangePicker
                                        initialSettings={{
                                            startDate: moment(formData.startDate, 'YYYY-MM-DD').toDate(),
                                            endDate: moment(formData.endDate, 'YYYY-MM-DD').toDate(),
                                            locale: {
                                                format: 'YYYY-MM-DD',
                                            },
                                        }}
                                        onEvent={handleEvent}
                                    >
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="dateRange"
                                            placeholder="Select Date Range"
                                            value={`${moment(formData.startDate).format('YYYY-MM-DD')} - ${moment(formData.endDate).format('YYYY-MM-DD')}`}
                                        />
                                    </DateRangePicker>
                                    <div className="input-group-append">
                                        <span className="input-group-text bg-white" onClick={handleClick}>
                                            <i className="ri-calendar-2-line"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-12 col-md-12">
                            <div className="form-group">
                                <label>Days of Week</label>
                                <div className="d-flex" style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
                                    {[ 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
                                        <div
                                            key={day}
                                            className="p-2"
                                            style={{
                                                flex: '0 0 calc(100% / 7)', // Each item takes 1/7th of the row
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <div className="custom-control custom-checkbox">
                                                <input
                                                    type="checkbox"
                                                    className="custom-control-input"
                                                    name={day}
                                                    id={day}
                                                    checked={formData.daysOfWeek?.includes(day)}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="custom-control-label" htmlFor={day}>{day}</label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 col-md-12">
                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea className="form-control" rows="5" name="description" value={formData.description} onChange={handleInputChange}></textarea>
                            </div>
                        </div>
                        <div className="col-lg-12 col-md-12">
                            <div className="form-group">
                                <label htmlFor="document">Document <small className="text-muted">(If any)</small></label>
                                <div className="custom-file">
                                    <input
                                        type="file"
                                        className="custom-file-input"
                                        id="uploadEmpofferletter"
                                        name="document"
                                        onChange={handleInputChange}
                                    />
                                    <label className="custom-file-label" htmlFor="uploadEmpofferletter">
                                        {
                                            (formData.medicalDocuments && formData.medicalDocuments.length > 0 ?
                                                formData.medicalDocuments[0].split('?')[0].split('/').pop() : "Choose file")
                                        }
                                    </label>
                                </div>
                            </div>
                        </div>



                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="managerEmail" className="required">Alternate Contact Person </label>
                                <input
                                    className="form-control"
                                    type="email"
                                    name="alternateContactPerson"
                                    value={formData.alternateContactPerson || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-6">
                            <div className="form-group">
                                <label htmlFor="managerEmail" className="required">Manager Email</label>
                                <input
                                    className="form-control"
                                    type="email"
                                    name="managerEmail"
                                    // value={formData.managerApprovalStatus?.[0]?.emailId || ''}
                                    value={formData.teamLeadEmail || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-center py-2">
                    <Button variant="light" onClick={handleModalClose}>Cancel</Button>
                    <Button variant="primary" onClick={handleSubmit}>Submit <i className="ri-checkbox-circle-line"></i></Button>
                </Modal.Footer>
            </Modal>


        </div>
    );
};

export default LeaveStatus;
